export const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });

export function getRadianAngle(degreeValue: number): number {
  return (degreeValue * Math.PI) / 180;
}

export function rotateSize(width: number, height: number, rotation: number) {
  const rotRad = getRadianAngle(rotation);

  return {
    width:
      Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height:
      Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

export interface PixelCrop {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface Flip {
  horizontal: boolean;
  vertical: boolean;
}

export default async function getCroppedImg(
  imageSrc: string,
  pixelCrop: PixelCrop,
  rotation = 0,
  flip: Flip = { horizontal: false, vertical: false }
): Promise<string | null> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    return null;
  }

  const rotRad = getRadianAngle(rotation);

  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
    image.width,
    image.height,
    rotation
  );

  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
  ctx.translate(-image.width / 2, -image.height / 2);

  ctx.drawImage(image, 0, 0);

  // Clamp the crop rectangle to the rendered-image bounds so any region the
  // user dragged past the edge (e.g. after zooming out) is dropped instead of
  // baked into the output as transparent padding — that padding otherwise
  // shows up as gray bars on any consumer surface with a non-white bg.
  const clampedX = Math.max(0, Math.min(pixelCrop.x, bBoxWidth));
  const clampedY = Math.max(0, Math.min(pixelCrop.y, bBoxHeight));
  const clampedRight = Math.max(clampedX, Math.min(pixelCrop.x + pixelCrop.width, bBoxWidth));
  const clampedBottom = Math.max(clampedY, Math.min(pixelCrop.y + pixelCrop.height, bBoxHeight));
  const clampedWidth = clampedRight - clampedX;
  const clampedHeight = clampedBottom - clampedY;

  const data = ctx.getImageData(
    clampedX,
    clampedY,
    clampedWidth,
    clampedHeight
  );

  let hasTransparency = false;
  for (let i = 3; i < data.data.length; i += 4) {
    if (data.data[i] < 255) {
      hasTransparency = true;
      break;
    }
  }

  canvas.width = clampedWidth;
  canvas.height = clampedHeight;

  ctx.putImageData(data, 0, 0);

  return hasTransparency
    ? canvas.toDataURL("image/png")
    : canvas.toDataURL("image/jpeg", 0.9);
}
